"use client";

import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { graphql } from "gql.tada";
import type { Client } from "urql";
import writeExcelFile, { type SheetData } from "write-excel-file";
import {
  type ChartData,
  generateChartData,
} from "@/app/[lang]/[workspaceId]/iw/components/generate-chart-data";
import type { IndexWorkerExportToolOutput } from "./output";

type URLExportRow = {
  url: string;
  crawled: boolean;
  indexed: boolean;
  status: string;
  isFailure: boolean;
  userCanonical: string;
  googleCanonical: string;
  crawledAs: string;
  lastCrawledAt: string;
  referer: string[];
};

type URLCompareExportRow = URLExportRow & {
  crawledCompare?: boolean;
  indexedCompare?: boolean;
  statusCompare?: string;
  isFailureCompare?: boolean;
  userCanonicalCompare?: string;
  googleCanonicalCompare?: string;
  crawledAsCompare?: string;
  lastCrawledAtCompare?: string;
  refererCompare?: string[];
};

type URLExportDataRow = {
  url: string;
  crawled: string;
  indexed: string;
  status_report: string;
  user_canonical: string;
  google_canonical: string;
  user_agent: string;
  refering_urls: string[];
  last_crawled_at: string;
};

type URLCompareExportDataRow = URLExportDataRow & {
  crawled_compare?: string;
  indexed_compare?: string;
  status_report_compare?: string;
  user_canonical_compare?: string;
  google_canonical_compare?: string;
  user_agent_compare?: string;
  refering_urls_compare?: string[];
  last_crawled_at_compare?: string;
};

const indexWorkerTrendsExportDataQuery = graphql(`
  query IndexWorkerExportData($id: ID!, $range: TimeRangeInput, $compareRange: TimeRangeInput, $isCompare: Boolean!) {
    node(id: $id) {
      ... on IndexWorker {
        id
        __typename
        name
        jobs(
          first: 10000
          orderby: { field: CREATED_AT, direction: DESC }
          range: $range
        ) {
          __typename
          edges {
            __typename
            node {
              id
              __typename
              createdAt
              completedAt
              requestCount
              failureCount
              result {
                crawledRate
                indexedRate
                sevenDaysMovingAverageCrawledRate
                sevenDaysMovingAverageIndexedRate
                twentyEightDaysMovingAverageCrawledRate
                twentyEightDaysMovingAverageIndexedRate
                crawledCount
                indexedCount
                totalCount
              }
            }
          }
        }
        compareJobs: jobs(
          first: 10000
          orderby: { field: CREATED_AT, direction: DESC }
          range: $compareRange
        ) @include(if: $isCompare) {
          __typename
          edges {
            __typename
            node {
              id
              __typename
              createdAt
              completedAt
              requestCount
              failureCount
              result {
                crawledRate
                indexedRate
                sevenDaysMovingAverageCrawledRate
                sevenDaysMovingAverageIndexedRate
                twentyEightDaysMovingAverageCrawledRate
                twentyEightDaysMovingAverageIndexedRate
                crawledCount
                indexedCount
                totalCount
              }
            }
          }
        }
      }
    }
  }
`);

const indexWorkerUrlsExportDataQuery = graphql(`
  query IndexWorkerUrlsExportData($id: ID!, $range: TimeRangeInput, $compareRange: TimeRangeInput, $isCompare: Boolean!) {
    node(id: $id) {
      ... on IndexWorker {
        id
        __typename
        name
        jobs(
          first: 1
          orderby: { field: CREATED_AT, direction: DESC }
          range: $range
        ) {
          edges {
            node {
              id
              __typename
              failureURLs {
                url
                error
              }
              tasks(first: 2000) {
                edges {
                  node {
                    crawled
                    indexed
                    result {
                      coverageState
                      crawledAs
                      googleCanonical
                      lastCrawlTime
                      refferingUrls
                      userCanonical
                    }
                    url {
                      id
                      __typename
                      url
                    }
                  }
                }
              }
            }
          }
        }
        compareJobs: jobs(
          first: 1
          orderby: { field: CREATED_AT, direction: DESC }
          range: $compareRange
        ) @include(if: $isCompare) {
          edges {
            node {
              id
              __typename
              failureURLs {
                url
                error
              }
              tasks(first: 2000) {
                edges {
                  node {
                    crawled
                    indexed
                    result {
                      coverageState
                      crawledAs
                      googleCanonical
                      lastCrawlTime
                      refferingUrls
                      userCanonical
                    }
                    url {
                      id
                      __typename
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

type TrendsExportRow = {
  date: string;
  crawledRate: number | null;
  indexedRate: number | null;
};

type TrendsExportDateRange = {
  start: string;
  end: string;
};

type FetchIndexWorkerTrendsExportDataInput = {
  indexWorkerId: string;
  dateRange?: TrendsExportDateRange | null;
  isComparing: boolean;
  comparingDateRange?: TrendsExportDateRange | null;
};

type FetchIndexWorkerTrendsExportDataResult = {
  indexWorkerName: string;
  data: ChartData[];
  compareData?: ChartData[];
};

/**
 * get_index_worker_export_data の exportRequest を GUI 同等のダウンロードとして実行する。
 */
export async function downloadIndexWorkerExportData(
  client: Client,
  output: IndexWorkerExportToolOutput,
): Promise<void> {
  if (output.exportRequest.exportTarget === "trends") {
    const exportData = await fetchIndexWorkerTrendsExportData(client, {
      indexWorkerId: output.exportRequest.indexWorkerId,
      dateRange: output.exportRequest.dateRange,
      isComparing: output.exportRequest.isComparing,
      comparingDateRange: output.exportRequest.comparingDateRange,
    });

    exportTrendsData(
      exportData.data,
      exportData.indexWorkerName || output.exportRequest.indexWorkerName,
      exportData.compareData,
    );
    return;
  }

  const urlData = await fetchIndexWorkerUrlsExportData(client, {
    indexWorkerId: output.exportRequest.indexWorkerId,
    dateRange: output.exportRequest.dateRange,
    isComparing: output.exportRequest.isComparing,
    comparingDateRange: output.exportRequest.comparingDateRange,
  });

  // Use the latest fetched IW name so the downloaded filename follows
  // the same user-visible name as the current GUI state.
  const indexWorkerName =
    urlData.indexWorkerName ||
    output.exportRequest.indexWorkerName ||
    output.context.indexWorkerName;

  if (output.exportRequest.isComparing) {
    await exportURLCompareData(
      urlData.combinedData,
      indexWorkerName,
      output.exportRequest.format,
    );
    return;
  }

  await exportURLData(
    urlData.data,
    indexWorkerName,
    output.exportRequest.format,
  );
}

/**
 * IWチャートの行データを、UIエクスポートと同一形式のCSVへ変換する。
 */
function convertTrendsDataToCsv(
  data: TrendsExportRow[],
  compareData?: TrendsExportRow[],
): string {
  if (data.length === 0) {
    return "";
  }

  const headers = ["date", "crawled_rate", "indexed_rate"];
  if (compareData) {
    headers.push(
      "compare_date",
      "compare_crawled_rate",
      "compare_indexed_rate",
    );
  }

  const csvRows = [headers.join(",")];
  const maxLength = Math.max(data.length, compareData?.length ?? 0);

  for (let i = 0; i < maxLength; i++) {
    const row = data[i];
    const compareRow = compareData?.[i];
    const values: string[] = [];

    if (row) {
      values.push(
        `"${row.date}"`,
        row.crawledRate !== null ? row.crawledRate.toFixed(4) : "",
        row.indexedRate !== null ? row.indexedRate.toFixed(4) : "",
      );
    } else {
      values.push("", "", "");
    }

    if (compareData) {
      if (compareRow) {
        values.push(
          `"${compareRow.date}"`,
          compareRow.crawledRate !== null
            ? compareRow.crawledRate.toFixed(4)
            : "",
          compareRow.indexedRate !== null
            ? compareRow.indexedRate.toFixed(4)
            : "",
        );
      } else {
        values.push("", "", "");
      }
    }

    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
}

/**
 * IWトレンドCSVのファイル名を生成する。
 */
function buildTrendsExportFilename(
  iwName: string,
  now: Date = new Date(),
): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  const dateTimeStr = `${year}${month}${day}${hours}${minutes}${seconds}`;
  const sanitizedIwName = iwName.replace(/\s+/g, "_");
  return `${sanitizedIwName}_trends_${dateTimeStr}.csv`;
}

function exportTrendsData(
  data: Parameters<typeof convertTrendsDataToCsv>[0],
  iwName: string,
  compareData?: Parameters<typeof convertTrendsDataToCsv>[1],
) {
  const csvContent = convertTrendsDataToCsv(data, compareData);
  const fileName = buildTrendsExportFilename(iwName);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
  saveAs(blob, fileName);
}

/**
 * IWトレンドCSVエクスポート用の全件データを、GUIと同条件で取得して整形する。
 */
async function fetchIndexWorkerTrendsExportData(
  client: Client,
  input: FetchIndexWorkerTrendsExportDataInput,
): Promise<FetchIndexWorkerTrendsExportDataResult> {
  const { indexWorkerId, dateRange, isComparing, comparingDateRange } = input;
  const { data: exportData, error } = await client.query(
    indexWorkerTrendsExportDataQuery,
    {
      id: indexWorkerId,
      range: dateRange ?? undefined,
      compareRange: comparingDateRange ?? undefined,
      isCompare: isComparing,
    },
  );

  if (error) {
    throw new Error(error.message);
  }

  if (exportData?.node?.__typename !== "IndexWorker") {
    throw new Error("Failed to fetch export data");
  }

  const fullData = generateChartData(exportData.node.jobs.edges);
  const fullCompareData =
    isComparing && exportData.node.compareJobs
      ? generateChartData(exportData.node.compareJobs.edges)
      : undefined;

  return {
    indexWorkerName: exportData.node.name,
    data: fullData,
    compareData: fullCompareData,
  };
}

/**
 * URLs カードの query/行変換と同条件でエクスポート用データを取得する。
 */
async function fetchIndexWorkerUrlsExportData(
  client: Client,
  input: {
    indexWorkerId: string;
    dateRange?: { start: string; end: string } | null;
    isComparing: boolean;
    comparingDateRange?: { start: string; end: string } | null;
  },
): Promise<{
  indexWorkerName: string;
  data: URLExportRow[];
  combinedData: URLCompareExportRow[];
}> {
  const { indexWorkerId, dateRange, isComparing, comparingDateRange } = input;
  const { data: exportData, error } = await client.query(
    indexWorkerUrlsExportDataQuery,
    {
      id: indexWorkerId,
      range: dateRange ?? undefined,
      compareRange: comparingDateRange ?? undefined,
      isCompare: isComparing,
    },
  );

  if (error) {
    throw new Error(error.message);
  }

  if (exportData?.node?.__typename !== "IndexWorker") {
    throw new Error("Failed to fetch URL export data");
  }

  const data = toURLTableRows(exportData.node.jobs.edges?.[0]?.node);
  const compareData = isComparing
    ? toURLTableRows(exportData.node.compareJobs?.edges?.[0]?.node)
    : [];

  const combinedData = data.map((item) => {
    const compareItem = compareData.find((ci) => ci.url === item.url);
    return {
      ...item,
      crawledCompare: compareItem?.crawled,
      indexedCompare: compareItem?.indexed,
      statusCompare: compareItem?.status,
      isFailureCompare: compareItem?.isFailure,
      userCanonicalCompare: compareItem?.userCanonical,
      googleCanonicalCompare: compareItem?.googleCanonical,
      crawledAsCompare: compareItem?.crawledAs,
      lastCrawledAtCompare: compareItem?.lastCrawledAt,
      refererCompare: compareItem?.referer,
    };
  });

  return {
    indexWorkerName: exportData.node.name,
    data,
    combinedData,
  };
}

function toURLTableRows(
  jobNode:
    | {
        tasks?: {
          edges?: Array<{
            node: {
              crawled?: boolean | null;
              indexed?: boolean | null;
              result?: {
                coverageState?: string | null;
                userCanonical?: string | null;
                googleCanonical?: string | null;
                crawledAs?: string | null;
                lastCrawlTime?: string | null;
                refferingUrls?: string[] | null;
              } | null;
              url: {
                id: string;
                url?: string | null;
              };
            };
          }>;
        } | null;
        failureURLs?: Array<{
          url: string;
          error: string;
        }> | null;
      }
    | null
    | undefined,
): URLExportRow[] {
  if (!jobNode) {
    return [];
  }

  const successRows: URLExportRow[] =
    jobNode.tasks?.edges?.map((edge) => ({
      url: edge.node.url.url ?? "N/A",
      crawled: edge.node.crawled ?? false,
      indexed: edge.node.indexed ?? false,
      status: edge.node.result?.coverageState || "N/A",
      isFailure: false,
      userCanonical: edge.node.result?.userCanonical || "N/A",
      googleCanonical: edge.node.result?.googleCanonical || "N/A",
      crawledAs: edge.node.result?.crawledAs || "N/A",
      lastCrawledAt: edge.node.result?.lastCrawlTime || "N/A",
      referer: edge.node.result?.refferingUrls ?? [],
    })) ?? [];

  const failureRows: URLExportRow[] =
    jobNode.failureURLs?.map((failureURL) => ({
      url: failureURL.url,
      crawled: false,
      indexed: false,
      status: failureURL.error,
      isFailure: true,
      userCanonical: "N/A",
      googleCanonical: "N/A",
      crawledAs: "N/A",
      lastCrawledAt: "N/A",
      referer: [],
    })) ?? [];

  return [...successRows, ...failureRows];
}

function exportURLData(
  data: URLExportRow[],
  iwName: string,
  format: "csv" | "excel" | "json",
): Promise<void> {
  const rows = convertToExportData(data);
  return writeExportFile(rows, iwName, format, false);
}

function exportURLCompareData(
  data: URLCompareExportRow[],
  iwName: string,
  format: "csv" | "excel" | "json",
): Promise<void> {
  const rows = convertToExportCompareData(data);
  return writeExportFile(rows, iwName, format, true);
}

function convertToExportData(data: URLExportRow[]): URLExportDataRow[] {
  return data.map((row) => {
    let crawledStatus = "not_crawled";
    if (row.crawled) {
      crawledStatus = "crawled";
    } else if (row.isFailure) {
      crawledStatus = "survey_error";
    }

    let indexedStatus = "not_indexed";
    if (row.indexed) {
      indexedStatus = "indexed";
    } else if (row.isFailure) {
      indexedStatus = "survey_error";
    }

    let userAgent = "not_specified";
    if (row.crawledAs === "MOBILE") {
      userAgent = "mobile";
    } else if (row.crawledAs === "DESKTOP") {
      userAgent = "desktop";
    }

    const referingUrls =
      row.referer.length > 0 && row.referer[0] !== "N/A" ? row.referer : [];

    const lastCrawledAt =
      row.lastCrawledAt !== "N/A"
        ? formatDateForExport(row.lastCrawledAt)
        : "-";

    return {
      url: row.url,
      crawled: crawledStatus,
      indexed: indexedStatus,
      status_report: row.status,
      user_canonical: row.userCanonical !== "N/A" ? row.userCanonical : "",
      google_canonical:
        row.googleCanonical !== "N/A" ? row.googleCanonical : "",
      user_agent: userAgent,
      refering_urls: referingUrls,
      last_crawled_at: lastCrawledAt,
    };
  });
}

function convertToExportCompareData(
  data: URLCompareExportRow[],
): URLCompareExportDataRow[] {
  return data.map((row) => {
    let crawledStatus = "not_crawled";
    if (row.crawled) {
      crawledStatus = "crawled";
    } else if (row.isFailure) {
      crawledStatus = "survey_error";
    }
    let crawledStatusCompare = "not_crawled";
    if (row.crawledCompare) {
      crawledStatusCompare = "crawled";
    } else if (row.isFailureCompare) {
      crawledStatusCompare = "survey_error";
    }

    let indexedStatus = "not_indexed";
    if (row.indexed) {
      indexedStatus = "indexed";
    } else if (row.isFailure) {
      indexedStatus = "survey_error";
    }
    let indexedStatusCompare = "not_indexed";
    if (row.indexedCompare) {
      indexedStatusCompare = "indexed";
    } else if (row.isFailureCompare) {
      indexedStatusCompare = "survey_error";
    }

    let userAgent = "not_specified";
    if (row.crawledAs === "MOBILE") {
      userAgent = "mobile";
    } else if (row.crawledAs === "DESKTOP") {
      userAgent = "desktop";
    }
    let userAgentCompare = "not_specified";
    if (row.crawledAsCompare === "MOBILE") {
      userAgentCompare = "mobile";
    } else if (row.crawledAsCompare === "DESKTOP") {
      userAgentCompare = "desktop";
    }

    const referingUrls =
      row.referer.length > 0 && row.referer[0] !== "N/A" ? row.referer : [];
    const referingUrlsCompare = !row.refererCompare
      ? []
      : row.refererCompare.length > 0 && row.refererCompare[0] !== "N/A"
        ? row.refererCompare
        : [];

    const lastCrawledAt =
      row.lastCrawledAt !== "N/A"
        ? formatDateForExport(row.lastCrawledAt)
        : "-";
    const lastCrawledAtCompare = !row.lastCrawledAtCompare
      ? "-"
      : row.lastCrawledAtCompare !== "N/A"
        ? formatDateForExport(row.lastCrawledAtCompare)
        : "-";

    return {
      url: row.url,
      crawled: crawledStatus,
      indexed: indexedStatus,
      status_report: row.status,
      user_canonical: row.userCanonical !== "N/A" ? row.userCanonical : "",
      google_canonical:
        row.googleCanonical !== "N/A" ? row.googleCanonical : "",
      user_agent: userAgent,
      refering_urls: referingUrls,
      last_crawled_at: lastCrawledAt,
      crawled_compare: crawledStatusCompare,
      indexed_compare: indexedStatusCompare,
      status_report_compare: row.statusCompare,
      user_canonical_compare:
        row.userCanonicalCompare !== "N/A" ? row.userCanonicalCompare : "",
      google_canonical_compare:
        row.googleCanonicalCompare !== "N/A" ? row.googleCanonicalCompare : "",
      user_agent_compare: userAgentCompare,
      refering_urls_compare: referingUrlsCompare,
      last_crawled_at_compare: lastCrawledAtCompare,
    };
  });
}

function formatDateForExport(dateStr: string): string {
  const parsed = dayjs(dateStr);
  if (!parsed.isValid()) {
    return dateStr;
  }
  return parsed.format("YYYY/MM/DD HH:mm:ss");
}

/**
 * Generates URL export filenames with the same format as IW detail GUI export:
 * `${iwName}_${yyyyMMddHHmmss}.${extension}` (excel => xlsx).
 */
function generateURLExportFileName(
  iwName: string,
  format: "csv" | "excel" | "json",
  now: Date = new Date(),
): string {
  const dateTimeStr = dayjs(now).format("YYYYMMDDHHmmss");
  const extension = format === "excel" ? "xlsx" : format;
  return `${iwName}_${dateTimeStr}.${extension}`;
}

async function writeExportFile(
  rows: Array<URLExportDataRow | URLCompareExportDataRow>,
  iwName: string,
  format: "csv" | "excel" | "json",
  isCompare: boolean,
): Promise<void> {
  const fileName = generateURLExportFileName(iwName, format);

  switch (format) {
    case "csv": {
      const csvContent = convertToCSV(rows, isCompare);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      saveAs(blob, fileName);
      return;
    }
    case "json": {
      const jsonContent = JSON.stringify(rows, null, 2);
      const blob = new Blob([jsonContent], {
        type: "application/json;charset=utf-8",
      });
      saveAs(blob, fileName);
      return;
    }
    case "excel": {
      const excelData = convertToExcel(rows, isCompare);
      await writeExcelFile(excelData, { fileName });
    }
  }
}

function convertToCSV(
  data: Array<URLExportDataRow | URLCompareExportDataRow>,
  isCompare: boolean,
): string {
  if (data.length === 0) return "";

  const headers = [
    "url",
    "crawled",
    "indexed",
    "refering_urls",
    "status_report",
    "user_canonical",
    "google_canonical",
    "user_agent",
    "last_crawled_at",
  ];
  if (isCompare) {
    headers.push(
      "crawled_compare",
      "indexed_compare",
      "refering_urls_compare",
      "status_report_compare",
      "user_canonical_compare",
      "google_canonical_compare",
      "user_agent_compare",
      "last_crawled_at_compare",
    );
  }

  const csvRows = [headers.join(",")];
  for (const row of data) {
    const values = headers.map((header) => {
      const value = row[header as keyof URLExportDataRow];
      if (Array.isArray(value)) {
        return `"${value.join(";")}"`;
      }
      if (typeof value === "string") {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
}

function convertToExcel(
  data: Array<URLExportDataRow | URLCompareExportDataRow>,
  isCompare: boolean,
): SheetData {
  const headerRow = [
    { value: "url" },
    { value: "crawled" },
    { value: "indexed" },
    { value: "status_report" },
    { value: "user_canonical" },
    { value: "google_canonical" },
    { value: "user_agent" },
    { value: "refering_urls" },
    { value: "last_crawled_at" },
  ];
  if (isCompare) {
    headerRow.push(
      { value: "crawled_compare" },
      { value: "indexed_compare" },
      { value: "status_report_compare" },
      { value: "user_canonical_compare" },
      { value: "google_canonical_compare" },
      { value: "user_agent_compare" },
      { value: "refering_urls_compare" },
      { value: "last_crawled_at_compare" },
    );
  }

  return [
    headerRow,
    ...data.map((row) => {
      const rowData = [
        { value: row.url },
        { value: row.crawled },
        { value: row.indexed },
        { value: row.status_report },
        { value: row.user_canonical },
        { value: row.google_canonical },
        { value: row.user_agent },
        { value: row.refering_urls.join(", ") },
        { value: row.last_crawled_at },
      ];
      if (isCompare) {
        const rowDataCompare = row as URLCompareExportDataRow;
        rowData.push(
          { value: rowDataCompare.crawled_compare ?? "" },
          { value: rowDataCompare.indexed_compare ?? "" },
          { value: rowDataCompare.status_report_compare ?? "" },
          { value: rowDataCompare.user_canonical_compare ?? "" },
          { value: rowDataCompare.google_canonical_compare ?? "" },
          { value: rowDataCompare.user_agent_compare ?? "" },
          { value: rowDataCompare.refering_urls_compare?.join(", ") ?? "" },
          { value: rowDataCompare.last_crawled_at_compare ?? "" },
        );
      }
      return rowData;
    }),
  ];
}
